// src/components/Modal.jsx
//
// Canonical modal chrome (§12 — Canonical Implementations Index).
//
// Provides: fixed overlay, card container, Escape key close,
// click-outside close (onMouseDown on overlay), focus-return to
// trigger element on close. Does NOT move focus inward on open —
// callers attach their own ref to the first focusable element.
//
// Optional white header: pass `title` to activate. The banner uses a white
// background (T.white) with a hairline bottom border per §19 of the style
// guide, dark bold title, optional subTitle line, and a × close button.
// When `title` is supplied and `ariaLabelledBy` is also provided, the
// title element receives that id so the dialog's aria-labelledby resolves.
//
// Props:
//   isOpen           {boolean}   — controls visibility (returns null when false)
//   onClose          {function}  — called on Escape, close ×, or click-outside
//   maxWidth         {number}    — optional, card max-width in px, default 720
//   children         {ReactNode} — card contents (body + footer)
//   testId           {string}    — optional data-testid on card div
//   overlayTestId    {string}    — optional data-testid on overlay div
//   ariaLabelledBy   {string}    — optional; when combined with `title`, placed
//                                  as id on the banner title element
//   disableBackdropClose {bool}  — default false; when true clicks on overlay
//                                  do not close the modal
//   title            {string}    — optional; activates blue-banner header
//   subTitle         {string}    — optional; second line inside banner header
//   closeTestId      {string}    — optional; data-testid on the banner × button
//   banner           {boolean}   — optional; uses the canonical blue header treatment

import React, { useEffect } from "react";
import { T, LAYERS } from "../app/tokens.js";

export function Modal({
  isOpen,
  onClose,
  maxWidth = 720,
  children,
  testId,
  overlayTestId,
  ariaLabelledBy,
  disableBackdropClose = false,
  title,
  subTitle,
  closeTestId,
  banner = false,
}) {
  // Capture the trigger element when opening; return focus to it on close.
  useEffect(() => {
    if (!isOpen) return undefined;
    const trigger = document.activeElement;
    return () => {
      if (trigger && typeof trigger.focus === "function") trigger.focus();
    };
  }, [isOpen]);

  // Escape key closes the modal.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: LAYERS.modal,
        background: T.modalOverlay,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20,
      }}
      onMouseDown={disableBackdropClose ? undefined : (e) => {
        if (e.target === e.currentTarget) {
          // Closing restores trigger focus; don't let this same mouse event
          // subsequently move it to the (now removed) non-focusable backdrop.
          e.preventDefault();
          onClose();
        }
      }}
      data-testid={overlayTestId}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={ariaLabelledBy}
        data-testid={testId}
        style={{
          background: T.white, color: T.dark,
          borderRadius: 14, overflow: "hidden",
          boxShadow: T.shadowModal,
          width: "100%", maxWidth, maxHeight: "90vh",
          display: "flex", flexDirection: "column",
        }}
      >
        {title && (
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "14px 18px", flexShrink: 0,
            background: banner
              ? `linear-gradient(90deg, ${T.hdrFrom}, ${T.hdrTo})`
              : T.white,
            borderBottom: banner ? "none" : `1px solid ${T.border}`,
          }}>
            <div>
              <div
                id={ariaLabelledBy}
                style={{
                  fontSize: T.fsMd5, fontWeight: T.fwBold,
                  color: banner ? T.white : T.dark,
                }}
              >
                {title}
              </div>
              {subTitle && (
                <div style={{
                  fontSize: T.fsXs5,
                  color: banner ? "rgba(255,255,255,0.78)" : T.mid,
                  marginTop: 2,
                }}>
                  {subTitle}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              data-testid={closeTestId}
              style={{
                background: "none", border: "none",
                color: banner ? T.white : T.mid, cursor: "pointer",
                fontSize: T.fsLg6, lineHeight: 1, padding: "0 4px",
                opacity: banner ? 0.85 : 1,
              }}
            >×</button>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}
